import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Users, TrendingUp, DollarSign, RotateCcw, FileText, Tag, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { useSession } from "@/App";
import { toast } from "sonner";

export default function AdminDashboardPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [tab, setTab] = useState("stats");
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [coupon, setCoupon] = useState({ code: "", discount_percent: "", discount_amount: "", max_uses: "", expires_at: "" });

  useEffect(() => {
    if (user === null) return; // session not yet loaded
    if (!user?.is_admin) {
      navigate("/app");
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [s, u, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/coupons"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setCoupons(c.data);
    } catch {
      toast.error("Errore caricamento dati admin");
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: coupon.code.toUpperCase(),
        discount_percent: coupon.discount_percent ? parseInt(coupon.discount_percent) : null,
        discount_amount: coupon.discount_amount ? parseFloat(coupon.discount_amount) : null,
        max_uses: coupon.max_uses ? parseInt(coupon.max_uses) : null,
        expires_at: coupon.expires_at || null,
      };
      await api.post("/admin/coupons", payload);
      toast.success("Coupon creato");
      setShowCouponForm(false);
      setCoupon({ code: "", discount_percent: "", discount_amount: "", max_uses: "", expires_at: "" });
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    }
  };

  if (!user?.is_admin) return null;

  // eslint-disable-next-line react/no-unstable-nested-components
  const StatCard = ({ icon: Icon, label, value, hint, accent }) => (
    <div className="bg-surface border border-subtle rounded-2xl p-5 animate-fade-up">
      <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={1.6} />
      <div className="font-display text-3xl font-semibold mt-3">{value}</div>
      <div className="text-xs text-tertiary-fg mt-1">{label}</div>
      {hint && <div className="text-[10px] text-tertiary-fg mt-1">{hint}</div>}
    </div>
  );

  return (
    <div className="px-5 md:px-10 py-8 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-brand" />
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Admin Dashboard</p>
      </div>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-6">Family Brain Business</h1>

      <div className="flex gap-2 border-b border-subtle mb-6">
        {[["stats", "Metriche"], ["users", "Utenti"], ["coupons", "Coupon"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === k ? "border-brand text-primary-fg" : "border-transparent text-tertiary-fg hover:text-primary-fg"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "stats" && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon={DollarSign} label="MRR" value={`€${stats.mrr}`} accent="var(--brand)" hint="Monthly Recurring Revenue" />
            <StatCard icon={TrendingUp} label="Revenue totale" value={`€${stats.total_revenue}`} accent="var(--sage)" />
            <StatCard icon={Users} label="Utenti totali" value={stats.total_users} accent="var(--info)" hint={`${stats.new_users_7d} nuovi negli ultimi 7gg`} />
            <StatCard icon={RotateCcw} label="Conversion rate" value={`${stats.conversion_rate}%`} accent="var(--amber)" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon={Users} label="Su Standard" value={stats.standard_active} accent="var(--brand)" />
            <StatCard icon={Users} label="Su Premium" value={stats.premium_active} accent="var(--amber)" />
            <StatCard icon={Users} label="In trial" value={stats.on_trial} accent="var(--sage)" />
            <StatCard icon={RotateCcw} label="Churn rate" value={`${stats.churn_rate}%`} accent="var(--rose)" hint={`${stats.churned_30d} negli ultimi 30gg`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={FileText} label="Documenti totali" value={stats.total_documents} accent="var(--text-secondary)" />
            <StatCard icon={FileText} label="Messaggi AI totali" value={stats.total_ai_messages} accent="var(--text-secondary)" />
          </div>
        </>
      )}

      {tab === "users" && (
        <div className="bg-surface border border-subtle rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-widest text-tertiary-fg border-b border-subtle">
            <div className="col-span-4">Utente</div>
            <div className="col-span-3">Famiglia</div>
            <div className="col-span-2">Piano</div>
            <div className="col-span-3">Registrato</div>
          </div>
          {users.map(u => (
            <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm border-b border-subtle/50 last:border-0">
              <div className="col-span-4 truncate">
                <div className="font-medium">{u.name} {u.is_admin && <span className="text-[10px] bg-brand text-white px-1.5 py-0.5 rounded ml-1">ADMIN</span>}</div>
                <div className="text-xs text-tertiary-fg truncate">{u.email}</div>
              </div>
              <div className="col-span-3 text-tertiary-fg truncate">{u.family_id?.slice(0, 8)}</div>
              <div className="col-span-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  u.plan === "premium" ? "bg-amber-warm/20 text-amber-warm" :
                  u.plan === "standard" ? "bg-brand/20 text-brand" :
                  "bg-elevated text-tertiary-fg"
                }`}>{u.plan || "free"}</span>
              </div>
              <div className="col-span-3 text-xs text-tertiary-fg">{new Date(u.created_at).toLocaleDateString("it-IT")}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "coupons" && (
        <>
          <button
            onClick={() => setShowCouponForm(true)}
            data-testid="admin-create-coupon-button"
            className="btn-primary mb-4 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuovo coupon
          </button>
          <div className="bg-surface border border-subtle rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs uppercase tracking-widest text-tertiary-fg border-b border-subtle">
              <div className="col-span-3">Codice</div>
              <div className="col-span-3">Sconto</div>
              <div className="col-span-2">Usi</div>
              <div className="col-span-2">Scadenza</div>
              <div className="col-span-2">Stato</div>
            </div>
            {coupons.map(c => (
              <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 text-sm border-b border-subtle/50 last:border-0">
                <div className="col-span-3 font-mono font-semibold text-brand">{c.code}</div>
                <div className="col-span-3">{c.discount_percent ? `${c.discount_percent}%` : c.discount_amount ? `€${c.discount_amount}` : "-"}</div>
                <div className="col-span-2 text-tertiary-fg">{c.used_count}/{c.max_uses || "∞"}</div>
                <div className="col-span-2 text-xs text-tertiary-fg">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("it-IT") : "-"}</div>
                <div className="col-span-2"><span className={c.active ? "text-sage" : "text-rose-warm"}>{c.active ? "Attivo" : "Disattivo"}</span></div>
              </div>
            ))}
            {coupons.length === 0 && <div className="text-center text-tertiary-fg py-8 text-sm">Nessun coupon. Creane uno!</div>}
          </div>
        </>
      )}

      {showCouponForm && (
        <div className="fixed inset-0 z-50 bg-app/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowCouponForm(false)}>
          <form onSubmit={createCoupon} onClick={(e) => e.stopPropagation()} className="bg-surface border border-subtle rounded-3xl p-6 max-w-md w-full space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-semibold">Nuovo coupon</h3>
              <button type="button" onClick={() => setShowCouponForm(false)} className="p-2 rounded-full hover:bg-elevated"><X className="w-4 h-4" /></button>
            </div>
            <input data-testid="coupon-form-code" required className="input-warm uppercase" placeholder="Codice (es. WELCOME20)" value={coupon.code} onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase() })} />
            <div className="flex gap-2">
              <input className="input-warm" placeholder="Sconto %" type="number" min="1" max="100" value={coupon.discount_percent} onChange={(e) => setCoupon({ ...coupon, discount_percent: e.target.value })} />
              <input className="input-warm" placeholder="o sconto €" type="number" step="0.01" value={coupon.discount_amount} onChange={(e) => setCoupon({ ...coupon, discount_amount: e.target.value })} />
            </div>
            <input className="input-warm" placeholder="Numero max usi (vuoto = ∞)" type="number" value={coupon.max_uses} onChange={(e) => setCoupon({ ...coupon, max_uses: e.target.value })} />
            <input className="input-warm" type="datetime-local" value={coupon.expires_at} onChange={(e) => setCoupon({ ...coupon, expires_at: e.target.value })} />
            <button data-testid="coupon-form-submit" type="submit" className="btn-primary w-full">Crea coupon</button>
          </form>
        </div>
      )}
    </div>
  );
}
